/**
 * CarouselHeader.jsx — Carousel Composer sticky top bar: views, photo sources,
 * the saved carousel, design toggle, shortcuts, export. The design bar lives
 * underneath it.
 *
 * On a phone the Library/Carousel toggle becomes the three steps — Pick,
 * Review, Export — and everything that will not fit at 390px moves out: the
 * photo sources go into the Pick step, and the saved carousel, the design bar
 * and the shortcut list into one overflow menu. Desktop is untouched.
 */

import { useState } from 'react';
import {
  openExport, setDesignOpen, setHelpOpen, setView, useCarousel,
} from '../../hooks/useCarouselStore';
import DesignBar from './DesignBar';

const STEPS = [['pick', 'Pick'], ['review', 'Review'], ['export', 'Export']];

export default function CarouselHeader({ headRef, mobile, step, onStep, onAddPhotos, onShoots, onDocs }) {
  const view = useCarousel((s) => s.view);
  const libN = useCarousel((s) => s.library.length);
  const slideN = useCarousel((s) => s.slides.length);
  const designOpen = useCarousel((s) => !!s.designOpen);
  const helpOpen = useCarousel((s) => s.helpOpen);
  const doc = useCarousel((s) => s.doc);
  const docDirty = useCarousel((s) => s.docDirty);
  const [menu, setMenu] = useState(false);

  if (mobile) {
    const counts = { pick: libN, review: slideN, export: 0 };
    return (
      <>
        <div className="cc-head" ref={headRef}>
          <div className="bar cc-mbar">
            <h1 className="cc-mtitle">{doc ? doc.title : 'Carousel Composer'}</h1>
            {doc && docDirty && <span className="cc-dot" aria-label="unsaved changes" />}
            <button
              type="button"
              className={'cc-mmore' + (menu ? ' on' : '')}
              aria-haspopup="menu"
              aria-expanded={menu}
              aria-label="More"
              onClick={() => setMenu(!menu)}
            >
              ⋯
            </button>
          </div>
          <div className="cc-steps" role="tablist" aria-label="Composer steps">
            {STEPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                className="cc-step"
                aria-selected={step === k}
                onClick={() => { setMenu(false); onStep(k); }}
              >
                {label}
                {counts[k] > 0 && <span className="mono cc-step-c">{counts[k]}</span>}
              </button>
            ))}
          </div>
          {designOpen && <DesignBar />}
        </div>
        {menu && (
          <>
            <button type="button" className="cc-oscrim" aria-label="Close menu" onClick={() => setMenu(false)} />
            <div className="cc-omenu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="cc-omi"
                onClick={() => { setMenu(false); onDocs(); }}
              >
                <span>Carousels</span>
                <span className="hint">{doc ? doc.title + (docDirty ? ' · unsaved' : ' · saved') : 'Save or open one'}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="cc-omi"
                aria-pressed={designOpen}
                onClick={() => { setMenu(false); setDesignOpen(!designOpen, true); }}
              >
                <span>Slide design</span>
                <span className="hint">{designOpen ? 'Showing below' : 'Shape, padding, fill'}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="cc-omi"
                aria-pressed={helpOpen}
                onClick={() => { setMenu(false); setHelpOpen(!helpOpen); }}
              >
                <span>Keyboard shortcuts</span>
                <span className="hint">With a keyboard attached</span>
              </button>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div className="cc-head" ref={headRef}>
      <div className="bar">
        <div className="brand">
          <h1>Carousel Composer</h1>
          <span className="tagline">Instagram slides from your own frames</span>
        </div>
        <div className="seg">
          <button type="button" aria-pressed={view === 'library'} onClick={() => setView('library')}>
            Library <span className="mono">{libN}</span>
          </button>
          <button type="button" aria-pressed={view === 'carousel'} onClick={() => setView('carousel')}>
            Carousel <span className="mono">{slideN}</span>
          </button>
        </div>
        {/* the two ways photos get in, grouped as one thing */}
        <div className="cc-group">
          <button type="button" className="btn tiny" onClick={onAddPhotos}>Add photos</button>
          <button type="button" className="btn tiny" onClick={onShoots} title="Load a delivered shoot">Shoots</button>
        </div>
        <span className="cc-divider" aria-hidden="true" />
        <button
          type="button"
          className={'btn tiny cc-doc-btn' + (doc ? ' is-open' : '')}
          onClick={onDocs}
          title={doc ? 'Saved carousel — ' + (docDirty ? 'unsaved changes' : 'up to date') : 'Save or open a carousel'}
        >
          <span className="cc-doc-name">{doc ? doc.title : 'Carousels'}</span>
          {doc && docDirty && <span className="cc-dot" aria-label="unsaved changes" />}
        </button>
        <button
          type="button"
          className="btn tiny design-toggle"
          aria-expanded={designOpen}
          aria-controls="cc-design-bar"
          onClick={() => setDesignOpen(!designOpen, true)}
        >
          Design
        </button>
        <button
          type="button"
          className="btn tiny ghost help-btn"
          aria-expanded={helpOpen}
          title="Keyboard shortcuts (?)"
          aria-label="Keyboard shortcuts"
          onClick={() => setHelpOpen(!helpOpen)}
        >
          ?
        </button>
        <button type="button" className="btn primary export-all" title="Export (E)" onClick={openExport}>Export</button>
      </div>
      {designOpen && <DesignBar />}
    </div>
  );
}
